import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BRAND } from "@/lib/constants";
import api, { getErrorMessage } from "@/lib/api";

// ---------------------------------------------------------------------------
// TwoFASendersPanel
// Renders a tenant-wide panel to declare which email addresses / domains
// their 2FA codes arrive from. Placed at the top of the connections catalog,
// above the portals table.
//
// API contract:
//   GET /api/catalog/2fa-senders → { configured: string[], suggestions: string[] }
//   PUT /api/catalog/2fa-senders  body { senders: string[] }
//              → { status: "ok"|"partial"|"saved_no_rule", ... }
// ---------------------------------------------------------------------------

export default function TwoFASendersPanel() {
  const [loading, setLoading] = useState(true);
  const [senders, setSenders] = useState([]);        // current chip list
  const [saved, setSaved] = useState([]);            // last-persisted list (for dirty check)
  const [suggestions, setSuggestions] = useState([]); // from API
  const [inputVal, setInputVal] = useState("");
  const [saving, setSaving] = useState(false);

  const inputRef = useRef(null);

  // ---- fetch on mount ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/catalog/2fa-senders");
        if (cancelled) return;
        const configured = res.data?.configured ?? [];
        const suggestionsRaw = res.data?.suggestions ?? [];
        setSenders(configured);
        setSaved(configured);
        setSuggestions(suggestionsRaw);
      } catch (err) {
        if (!cancelled) {
          toast.error(getErrorMessage(err, "Failed to load 2FA senders"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ---- derived ----
  const isDirty =
    senders.length !== saved.length ||
    [...senders].sort().join(",") !== [...saved].sort().join(",");

  // Suggestions not yet added
  const pendingSuggestions = suggestions.filter(
    (s) => !senders.includes(s.trim().toLowerCase())
  );

  // ---- helpers ----
  function normalize(val) {
    return val.trim().toLowerCase();
  }

  function addEntry(raw) {
    const val = normalize(raw);
    if (!val) return;
    if (senders.includes(val)) return; // duplicate
    setSenders((prev) => [...prev, val]);
    setInputVal("");
  }

  function removeEntry(val) {
    setSenders((prev) => prev.filter((s) => s !== val));
  }

  function handleKeyDown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      addEntry(inputVal);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      const res = await api.put("/catalog/2fa-senders", { senders });
      const status = res.data?.status;
      setSaved([...senders]);
      if (status === "saved_no_rule") {
        toast.info("Saved — takes effect once this tenant's mailbox is provisioned");
      } else {
        toast.success("2FA senders saved");
      }
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to save 2FA senders"));
    } finally {
      setSaving(false);
    }
  }

  // ---- render ----
  return (
    <section
      className="rounded-lg border bg-white p-5 space-y-4"
      aria-label="2FA code senders"
    >
      {/* Header */}
      <div>
        <h2 className="text-base font-semibold text-gray-900">2FA code senders</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Email addresses or domains your portals send verification codes from.
          We route those into your 2FA folder.
        </p>
      </div>

      {/* Body */}
      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-8 w-1/2" />
          <Skeleton className="h-8 w-1/3" />
        </div>
      ) : (
        <>
          {/* Chips */}
          {senders.length === 0 ? (
            <p className="text-sm text-gray-400">No 2FA senders configured yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {senders.map((s) => (
                <Badge
                  key={s}
                  className="inline-flex items-center gap-1 font-medium rounded-full border hover:bg-opacity-80"
                  style={{
                    backgroundColor: BRAND.soft,
                    borderColor: BRAND.border,
                    color: BRAND.text,
                  }}
                >
                  {s}
                  <button
                    type="button"
                    aria-label={`Remove ${s}`}
                    className="ml-0.5 rounded-full hover:bg-black/10 focus:outline-none focus-visible:ring-2"
                    style={{ color: BRAND.text }}
                    onClick={() => removeEntry(s)}
                  >
                    &#10005;
                  </button>
                </Badge>
              ))}
            </div>
          )}

          {/* Add row */}
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              ref={inputRef}
              type="text"
              placeholder="e.g. noreply@moonfare.com or moonfare.com"
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-72 max-w-full"
              aria-label="New 2FA sender address or domain"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => addEntry(inputVal)}
              disabled={!inputVal.trim()}
            >
              Add
            </Button>

            {/* Suggestions dropdown — only when there's something to suggest */}
            {pendingSuggestions.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    Suggestions &#9660;
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {pendingSuggestions.map((s) => (
                    <DropdownMenuItem
                      key={s}
                      className="cursor-pointer text-sm"
                      onSelect={() => addEntry(s)}
                    >
                      {s}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Save */}
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              className="text-white"
              style={{ backgroundColor: BRAND.bg }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BRAND.hover)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND.bg)}
              disabled={!isDirty || saving}
              onClick={handleSave}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
            {isDirty && (
              <span className="text-xs text-gray-400">Unsaved changes</span>
            )}
          </div>
        </>
      )}
    </section>
  );
}
